import React from 'react';
import { Link } from 'react-router-dom';

function ApplyVisa() {
  return (
    <article className="seo-page-container" style={{ 
      padding: 'clamp(15px, 4vw, 30px)', 
      background: '#fff', 
      margin: 'clamp(10px, 3vw, 20px)', 
      borderRadius: '4px', 
      textAlign: 'left', 
      fontFamily: 'Arial, sans-serif',
      boxSizing: 'border-box'
    }}>
      
      {/* HEADER HERO SECTION */}
      <header>
        <h1 style={{ 
          color: '#0b3c5d', 
          marginBottom: '15px', 
          fontSize: 'clamp(1.5rem, 6vw, 2.4rem)', 
          fontWeight: '700', 
          lineHeight: '1.3' 
        }}>
          How to Apply for an Indian Medical Visa: Complete International Patient Guide
        </h1>
        <p style={{ color: '#444', lineHeight: '1.8', fontSize: 'clamp(0.95rem, 3.5vw, 1.1rem)', marginBottom: '25px' }}>
          Securing your travel documentation is one of the most vital logistical milestones when planning an overseas surgical or clinical trip. For international patients journeying to India for advanced medical treatment, the Government of India offers specialized visa pathways designed to streamline border custom entries. 
          This step-by-step navigational article highlights **Indian Medical Visa requirements, application processing timelines, mandatory checklists, and medical attendant regulations** to help traveling patients research their itinerary safely.
        </p>
      </header>

      {/* CALL TO ACTION BUTTON */}
      <div style={{ marginBottom: '35px' }}>
        <Link to="/submit-query" style={{ 
          background: '#28a745', 
          color: '#fff', 
          padding: '14px 24px', 
          textDecoration: 'none', 
          borderRadius: '4px', 
          fontWeight: 'bold', 
          display: 'inline-block', 
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          fontSize: '0.95rem',
          textAlign: 'center',
          boxSizing: 'border-box',
          maxWidth: '100%'
        }}>
          Download Visa Documentation Templates & Checklists (Free) →
        </Link>
      </div>

      {/* SECTION 1: OVERVIEW OF THE MEDICAL VISA (MED VISA) */}
      <section style={{ marginBottom: '35px' }}>
        <h2 style={{ color: '#0b3c5d', borderBottom: '2px solid #f4f8fb', paddingBottom: '8px', fontSize: 'clamp(1.2rem, 5vw, 1.5rem)' }}>
          What is the Indian Medical Visa (Triple-Entry System)?
        </h2>
        <p style={{ color: '#555', lineHeight: '1.7', marginBottom: '15px', fontSize: '0.95rem' }}>
          The specialized Indian Medical Visa (classified as the **Med Visa**) is granted exclusively to foreign citizens traveling to India for purposeful healthcare interventions at recognized, certified multi-specialty hubs. 
        </p>
        <p style={{ color: '#555', lineHeight: '1.7', margin: 0, fontSize: '0.95rem' }}>
          A standard Indian Medical Visa is usually structured as a **triple-entry visa**, granting patients the legal flexibility to travel back to their home country up to three times during their approved recovery or treatment block if secondary follow-up consultations are requested. Additionally, up to two close family members or legal guardians can accompany the patient by obtaining a corresponding **Medical Attendant Visa (Med-X Visa)**.
        </p>
      </section>

      {/* SECTION 2: REQUIRED DOCUMENTATION MATRIX */}
      <section style={{ marginBottom: '35px' }}>
        <h2 style={{ color: '#0b3c5d', borderBottom: '2px solid #f4f8fb', paddingBottom: '8px', fontSize: 'clamp(1.2rem, 5vw, 1.5rem)' }}>
          Mandatory Indian Medical Visa Document Checklist
        </h2>
        <p style={{ color: '#555', lineHeight: '1.6', fontSize: '0.95rem' }}>
          When compiling files for submission via official sovereign immigration pathways, international patients are generally required to gather the following regulatory records:
        </p>

        <div style={{ background: '#f4f8fb', padding: 'clamp(15px, 4vw, 20px)', margin: '25px 0', borderRadius: '4px', borderLeft: '4px solid #005ba1' }}>
          <h3 style={{ marginTop: '0', color: '#005ba1', fontSize: '1.2rem', marginBottom: '10px' }}>📋 Required Consular Paperwork Checklist:</h3>
          <ul style={{ marginTop: '10px', listStyleType: 'square', paddingLeft: '20px', lineHeight: '1.8', color: '#333', fontSize: '0.95rem' }}>
            <li><strong>Official Hospital Invitation Letter (VIL):</strong> A formal, stamped Medical Visa Invitation Letter from the designated treatment facility in India, detailing the patient's identity, clinical diagnosis, and expected length of stay.</li>
            <li><strong>Local Diagnostic History:</strong> Comprehensive medical notes, diagnostic imaging reports, or prescription histories provided by physicians in your home country confirming the necessity of traveling for care.</li>
            <li><strong>Passport Validity:</strong> An original, legal passport holding a minimum validity of 6 months from your planned date of entry, containing at least two blank immigration pages.</li>
            <li><strong>Financial Solvency Evidence:</strong> Recent bank account records, income statements, or proof of funds demonstrating the capability to completely settle expected healthcare costs and local living expenditures.</li>
          </ul>
        </div>
      </section>

      {/* SECTION 3: RECOGNIZED REASONS FOR MEDICAL TRAVEL - RESPONSIVE MOBILE GRID */}
      <section style={{ marginBottom: '35px' }}>
        <h2 style={{ color: '#0b3c5d', borderBottom: '2px solid #f4f8fb', paddingBottom: '8px', fontSize: 'clamp(1.2rem, 5vw, 1.5rem)' }}>
          Approved Medical Categories for Immigration Entry
        </h2>
        <p style={{ color: '#555', lineHeight: '1.6', fontSize: '0.95rem' }}>
          Because Medical Trip World functions strictly as an open informational venue and independent directory, we remind traveling patients that sovereign immigration boards prioritize visa issuance for specialized, critical clinical fields, including:
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '20px', marginTop: '15px' }}>
          <div style={{ padding: '15px', background: '#fafafa', borderRadius: '4px', border: '1px solid #eee' }}>
            <h4 style={{ margin: '0 0 8px 0', color: '#0b3c5d', fontSize: '1.05rem' }}>Advanced Specialty Treatments</h4>
            <p style={{ margin: '0', fontSize: '0.9rem', color: '#666', lineHeight: '1.5' }}>
              Includes advanced cardiothoracic surgeries (like heart bypass or angioplasty), complex neurosurgery, specialized oncology protocols, and multi-joint orthopedic replacements.
            </p>
          </div>
          <div style={{ padding: '15px', background: '#fafafa', borderRadius: '4px', border: '1px solid #eee' }}>
            <h4 style={{ margin: '0 0 8px 0', color: '#0b3c5d', fontSize: '1.05rem' }}>Organ Transplants & Traditional Care</h4>
            <p style={{ margin: '0', fontSize: '0.9rem', color: '#666', lineHeight: '1.5' }}>
              Covers extensive bone marrow or organ transplant procedures, alongside holistic wellness programs under monitored traditional Ayurvedic therapy protocols.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 4: IN-DEPTH FAQ */}
      <section style={{ marginBottom: '35px', background: '#fff', border: '1px solid #e0e0e0', padding: 'clamp(15px, 4vw, 20px)', borderRadius: '4px' }}>
        <h3 style={{ color: '#0b3c5d', marginTop: '0', marginBottom: '20px', fontSize: '1.25rem' }}>
          Frequently Asked Questions (FAQs) – Indian Medical Visa Logistics
        </h3>
        
        <div style={{ marginBottom: '20px' }}>
          <p style={{ fontWeight: 'bold', color: '#333', margin: '0 0 5px 0', fontSize: '0.95rem' }}>Can international patients apply for an Indian Medical Visa online?</p>
          <p style={{ color: '#666', margin: '0', fontSize: '0.9rem', lineHeight: '1.6' }}>
            Yes. Citizens of eligible countries can apply for an e-Medical Visa (electronic visa) completely online via the official Government of India immigration portal, bypassing the need to physically visit an embassy or consulate.
          </p>
        </div>
        
        <div style={{ marginBottom: '20px' }}>
          <p style={{ fontWeight: 'bold', color: '#333', margin: '0 0 5px 0', fontSize: '0.95rem' }}>What is the standard processing time for an e-Medical Visa to India?</p>
          <p style={{ color: '#666', margin: '0', fontSize: '0.9rem', lineHeight: '1.6' }}>
            Once submitted online with correct documentation (including the mandatory Hospital Invitation Letter), a digital e-Medical Visa is typically reviewed and processed within 3 to 5 business days.
          </p>
        </div>
        
        <div style={{ marginBottom: '5px' }}>
          <p style={{ fontWeight: 'bold', color: '#333', margin: '0 0 5px 0', fontSize: '0.95rem' }}>Does Medical Trip World issue official Hospital Invitation Letters?</p>
          <p style={{ color: '#666', margin: '0', fontSize: '0.9rem', lineHeight: '1.6' }}>
            No. Medical Trip World is an independent informational platform and directory. We do not provide medical services, manage visa approvals, or issue formal invitation letters. Official Visa Invitation Letters can only be issued directly by the certified hospital or clinic you select for your treatment.
          </p>
        </div>
      </section>

      {/* RE-ENGAGEMENT FOOTER */}
      <footer style={{ borderTop: '1px solid #eee', paddingTop: '20px', textAlign: 'center' }}>
        <p style={{ fontSize: '1.05rem', fontWeight: 'bold', color: '#333', margin: '0 0 15px 0' }}>Begin Compiling Your Medical Travel Documentation Safely</p>
        <div style={{ display: 'flex', gap: '12px', flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center' }}>
          <style>{`
            @media (max-width: 550px) {
              .footer-btn { width: 100% !important; text-align: center; box-sizing: border-box; }
            }
          `}</style>
          <Link to="/submit-query" className="footer-btn" style={{ background: '#28a745', color: '#fff', padding: '12px 20px', textDecoration: 'none', borderRadius: '4px', fontWeight: 'bold', fontSize: '0.9rem', display: 'inline-block' }}>
            Submit General Information Request
          </Link>
          <Link to="/compare-costs" className="footer-btn" style={{ background: '#fff', color: '#0b3c5d', border: '2px solid #0b3c5d', padding: '10px 20px', textDecoration: 'none', borderRadius: '4px', fontWeight: 'bold', fontSize: '0.9rem', display: 'inline-block' }}>
            View Estimated Cost Charts
          </Link>
        </div>
      </footer>

    </article>
  );
}

export default ApplyVisa;